import { useQueryClient } from "@tanstack/react-query";
import { BrainCircuit, CheckCircle2, Lightbulb, Plus, X } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../components/StateViews";
import { StatusPill } from "../components/StatusPill";
import { useFilters } from "../contexts/FiltersContext";
import { appConfig } from "../lib/config";
import { createDecision, createLesson } from "../lib/data";
import { useEstufaData } from "../lib/queries";

export function IntelligencePage() {
  const query = useEstufaData();
  const { filter } = useFilters();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<"decision" | "lesson" | null>(null);
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState />;
  const data = query.data;
  const winners = data.decisions.filter((item) => item.verdict === "win");
  const losses = data.decisions.filter((item) => item.verdict === "loss");
  const unresolved = data.decisions.filter(
    (item) => !item.verdict || item.verdict === "inconclusive",
  );
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["estufa-data"] });
    setModal(null);
  };
  return (
    <>
      <PageHeader
        eyebrow="Memória da fábrica"
        title="Intelligence"
        description="O que aprendemos, o que repetimos e quais erros não merecem uma segunda temporada."
        action={
          <div className="button-row">
            <button type="button" className="secondary-button" onClick={() => setModal("lesson")}>
              <Lightbulb size={16} />
              Novo aprendizado
            </button>
            <button type="button" className="primary-button" onClick={() => setModal("decision")}>
              <Plus size={16} />
              Nova decisão
            </button>
          </div>
        }
      />
      {appConfig.demoMode ? (
        <div className="notice-card">
          <BrainCircuit size={21} />
          <div>
            <strong>Modo demonstração</strong>
            <p>Formulários ficam bloqueados para não confundir fixtures com dados observados.</p>
          </div>
        </div>
      ) : null}
      <div className="intelligence-scoreboard">
        <div>
          <span>Decisões vencedoras</span>
          <strong>{winners.length}</strong>
        </div>
        <div>
          <span>Decisões com perda</span>
          <strong>{losses.length}</strong>
        </div>
        <div>
          <span>Inconclusivas / abertas</span>
          <strong>{unresolved.length}</strong>
        </div>
        <div>
          <span>Aprendizados</span>
          <strong>{data.lessons.length}</strong>
        </div>
      </div>
      <div className="two-column-content">
        <section className="table-card">
          <div className="card-heading">
            <div>
              <h3>Decision Log</h3>
              <p>Hipótese → mudança → resultado → verdict.</p>
            </div>
          </div>
          {data.decisions.length === 0 ? (
            <EmptyState title="Nenhuma decisão registrada" />
          ) : (
            <div className="card-list">
              {data.decisions.map((decision) => (
                <article className="learning-card" key={decision.id}>
                  <div className="learning-meta">
                    <span>{decision.category}</span>
                    {decision.verdict ? (
                      <StatusPill value={decision.verdict} />
                    ) : (
                      <span>aguardando evidência</span>
                    )}
                  </div>
                  <strong>{decision.title}</strong>
                  <p>{decision.changeSummary}</p>
                  <div className="learning-details">
                    <span>
                      <b>Por quê:</b> {decision.rationale}
                    </span>
                    {decision.targetMetric ? (
                      <span>
                        <b>Métrica:</b> {decision.targetMetric}
                      </span>
                    ) : null}
                    {decision.observedResult ? (
                      <span>
                        <b>Resultado:</b> {decision.observedResult}
                      </span>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        <section className="table-card">
          <div className="card-heading">
            <div>
              <h3>Lessons</h3>
              <p>Inclui acertos e tombos. Ambos pagam aluguel aqui.</p>
            </div>
          </div>
          {data.lessons.length === 0 ? (
            <EmptyState title="Nenhum aprendizado registrado" />
          ) : (
            <div className="card-list">
              {data.lessons.map((lesson) => (
                <article className="learning-card" key={lesson.id}>
                  <div className="learning-meta">
                    <StatusPill value={lesson.evidenceType} />
                    <span>{lesson.confidence ?? "—"}% confiança</span>
                  </div>
                  <strong>{lesson.lesson}</strong>
                  <p>{lesson.whatHappened}</p>
                  {lesson.whatToRepeat ? (
                    <div className="lesson-good">
                      <CheckCircle2 size={15} />
                      <span>{lesson.whatToRepeat}</span>
                    </div>
                  ) : null}
                  {lesson.whatNotToRepeat ? (
                    <div className="lesson-bad">
                      <X size={15} />
                      <span>{lesson.whatNotToRepeat}</span>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
      {modal === "decision" ? (
        <DecisionModal
          seedId={filter.seedId ?? data.seeds[0]?.id ?? null}
          deploymentId={filter.deploymentId ?? null}
          onClose={() => setModal(null)}
          onSaved={refresh}
          disabled={appConfig.demoMode}
        />
      ) : null}
      {modal === "lesson" ? (
        <LessonModal
          seedId={filter.seedId ?? data.seeds[0]?.id ?? null}
          onClose={() => setModal(null)}
          onSaved={refresh}
          disabled={appConfig.demoMode}
        />
      ) : null}
    </>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose}>
            <X size={19} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DecisionModal({
  seedId,
  deploymentId,
  onClose,
  onSaved,
  disabled,
}: {
  seedId: string | null;
  deploymentId: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  disabled: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled) return;
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      await createDecision({
        seedId,
        deploymentId,
        title: String(form.get("title")),
        category: String(form.get("category")),
        hypothesis: String(form.get("hypothesis") || "") || null,
        changeSummary: String(form.get("changeSummary")),
        rationale: String(form.get("rationale")),
        targetMetric: String(form.get("targetMetric") || "") || null,
      });
      await onSaved();
    } catch {
      setError("Não foi possível salvar a decisão.");
      setSaving(false);
    }
  };
  return (
    <ModalShell title="Registrar decisão" onClose={onClose}>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <label>
          Título
          <input name="title" required maxLength={180} />
        </label>
        <label>
          Categoria
          <select name="category" defaultValue="product">
            {[
              "research",
              "product",
              "ux",
              "pricing",
              "trial",
              "listing",
              "seo",
              "distribution",
              "onboarding",
              "reliability",
              "support",
              "billing",
              "platform",
            ].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Hipótese
          <textarea name="hypothesis" rows={2} />
        </label>
        <label>
          O que mudou
          <textarea name="changeSummary" required rows={3} />
        </label>
        <label>
          Por que decidimos
          <textarea name="rationale" required rows={3} />
        </label>
        <label>
          Métrica alvo
          <input name="targetMetric" />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="primary-button" disabled={saving || disabled}>
          {disabled ? "Bloqueado no demo" : saving ? "Salvando…" : "Salvar decisão"}
        </button>
      </form>
    </ModalShell>
  );
}

function LessonModal({
  seedId,
  onClose,
  onSaved,
  disabled,
}: {
  seedId: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  disabled: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled) return;
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    const conf = Number(form.get("confidence"));
    try {
      await createLesson({
        sourceSeedId: seedId,
        category: String(form.get("category")),
        evidenceType: String(form.get("evidenceType")) as "observed" | "inference" | "hypothesis",
        confidence: Number.isFinite(conf) ? conf : null,
        lesson: String(form.get("lesson")),
        whatHappened: String(form.get("whatHappened")),
        whyWeThinkItHappened: String(form.get("why") || "") || null,
        whatToRepeat: String(form.get("repeat") || "") || null,
        whatNotToRepeat: String(form.get("avoid") || "") || null,
        appliesTo: String(form.get("appliesTo") || "") || null,
      });
      await onSaved();
    } catch {
      setError("Não foi possível salvar o aprendizado.");
      setSaving(false);
    }
  };
  return (
    <ModalShell title="Registrar aprendizado" onClose={onClose}>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <label>
          Aprendizado
          <input name="lesson" required maxLength={240} />
        </label>
        <div className="form-grid">
          <label>
            Categoria
            <input name="category" required defaultValue="product" />
          </label>
          <label>
            Tipo de evidência
            <select name="evidenceType" defaultValue="observed">
              <option value="observed">observed</option>
              <option value="inference">inference</option>
              <option value="hypothesis">hypothesis</option>
            </select>
          </label>
        </div>
        <label>
          O que aconteceu
          <textarea name="whatHappened" required rows={3} />
        </label>
        <label>
          Por que achamos que aconteceu
          <textarea name="why" rows={2} />
        </label>
        <div className="form-grid">
          <label>
            O que repetir
            <textarea name="repeat" rows={2} />
          </label>
          <label>
            O que evitar
            <textarea name="avoid" rows={2} />
          </label>
        </div>
        <div className="form-grid">
          <label>
            Aplica-se a<input name="appliesTo" />
          </label>
          <label>
            Confiança (0-100)
            <input name="confidence" type="number" min="0" max="100" defaultValue="70" />
          </label>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="primary-button" disabled={saving || disabled}>
          {disabled ? "Bloqueado no demo" : saving ? "Salvando…" : "Salvar aprendizado"}
        </button>
      </form>
    </ModalShell>
  );
}
